import { useCallback, useEffect, useRef, useState } from "react";
import type React from "react";

export type CarouselOptions = {
	/** Número de slides. */
	count: number;
	/** Intervalo do autoplay em ms; 0 desliga. */
	autoPlayMs?: number;
	/** Distância mínima de arrasto (px) para trocar de slide. */
	dragThreshold?: number;
};

export type CarouselApi = {
	index: number;
	/** Deslocamento do arrasto em curso, em px. */
	dragOffset: number;
	isDragging: boolean;
	next: () => void;
	previous: () => void;
	goTo: (index: number) => void;
	pause: () => void;
	resume: () => void;
	/** Handlers a espalhar no elemento que recebe o arrasto. */
	dragHandlers: {
		onPointerDown: (event: React.PointerEvent<HTMLElement>) => void;
		onPointerMove: (event: React.PointerEvent<HTMLElement>) => void;
		onPointerUp: (event: React.PointerEvent<HTMLElement>) => void;
		onPointerCancel: (event: React.PointerEvent<HTMLElement>) => void;
	};
};

/**
 * Carrossel de um slide por vez, com laço infinito, autoplay pausável e
 * arrasto por ponteiro. Sem dependência de UI: quem consome decide a renderização.
 */
export function useCarousel({
	count,
	autoPlayMs = 6000,
	dragThreshold = 60,
}: CarouselOptions): CarouselApi {
	const [index, setIndex] = useState(0);
	const [dragOffset, setDragOffset] = useState(0);
	const [isDragging, setIsDragging] = useState(false);
	const [isPaused, setIsPaused] = useState(false);
	const startX = useRef<number | null>(null);

	const step = useCallback(
		(delta: number) => {
			if (count < 1) return;
			setIndex((current) => (((current + delta) % count) + count) % count);
		},
		[count],
	);

	const next = useCallback(() => step(1), [step]);
	const previous = useCallback(() => step(-1), [step]);
	const goTo = useCallback(
		(target: number) => {
			if (count < 1) return;
			setIndex(Math.min(Math.max(target, 0), count - 1));
		},
		[count],
	);

	const pause = useCallback(() => setIsPaused(true), []);
	const resume = useCallback(() => setIsPaused(false), []);

	useEffect(() => {
		if (!autoPlayMs || isPaused || isDragging || count < 2) return;
		const timer = window.setInterval(next, autoPlayMs);
		return () => window.clearInterval(timer);
	}, [autoPlayMs, isPaused, isDragging, count, next]);

	const onPointerDown = useCallback((event: React.PointerEvent<HTMLElement>) => {
		if (event.pointerType === "mouse" && event.button !== 0) return;
		startX.current = event.clientX;
		setIsDragging(true);
	}, []);

	const onPointerMove = useCallback((event: React.PointerEvent<HTMLElement>) => {
		if (startX.current === null) return;
		setDragOffset(event.clientX - startX.current);
	}, []);

	const endDrag = useCallback(() => {
		if (startX.current === null) return;
		if (Math.abs(dragOffset) > dragThreshold) step(dragOffset < 0 ? 1 : -1);
		startX.current = null;
		setDragOffset(0);
		setIsDragging(false);
	}, [dragOffset, dragThreshold, step]);

	return {
		index,
		dragOffset,
		isDragging,
		next,
		previous,
		goTo,
		pause,
		resume,
		dragHandlers: {
			onPointerDown,
			onPointerMove,
			onPointerUp: endDrag,
			onPointerCancel: endDrag,
		},
	};
}
